import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import '@/assets/scss/style.scss'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Intro } from '@/components/Intro'
import { ScanResults } from '@/components/ScanResults'
import { EmailForm } from '@/components/EmailForm'
import Layout from './components/Layout'
import TermsOfUse from './components/Terms/TermsOfUse'
import PrivacyPolicy from './components/Terms/PrivacyPolicy'
import ReactPixel from 'react-facebook-pixel'
import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { PaymentResult } from '@/components/Checkout/SolidGate/PaymentResult'
import TestPaymentFormPage from '@/components/Checkout/SolidGate/TestPaymentFormPage'
import { PrivacyPolicyApp } from '@/components/Terms/PrivacyPolicyApp'
import TermsOfUseApp from '@/components/Terms/TermsOfUseApp'
import { TermsApp } from '@/components/Terms/TermsApp'
import PaywallOnePlan from '@/components/Paywall/PaywallOnePlan'
import { persistTrafficParams } from '@/helpers/propeller'
import { QuizStep } from '@/components/QuizStep'
import TermsOfUseQuiz from './components/quiz/TermsOfUse'
import PrivacyPolicyQuiz from './components/quiz/PrivacyPolicy'
import { AlternativeQuiz } from '@/data/MultiStepQuiz/alternativeQuiz'
import { Quiz } from '@/data/MultiStepQuiz/quiz'
import TermsOfUseUS from '@/components/Terms/TermsOfUseUS'
import PrivacyPolicyUS from '@/components/Terms/PrivacyPolicyUS'
import { getCountryCode, isEU } from '@/helpers/getCountryCode'
import PrivacyPolicyEU from '@/components/Terms/PrivacyPolicyEU'
import TermsOfUseEU from '@/components/Terms/TermsOfUseEU'

const ThankYou = lazy(() => import('./components/ThankYou/ThankYou'))
const Paywall = lazy(() => import('./components/Paywall/Paywall'))
const Upsell = lazy(() => import('./components/Upsell'))

const mainPixelId = process.env.REACT_APP_FB_PIXEL_ID ?? ''
const secondPixelId = process.env.REACT_APP_FB_PIXEL_SECOND_ID ?? ''

persistTrafficParams()

const url = new URL(window.location.href)

const email = url.searchParams.get('email')
const fbc = url.searchParams.get('fbc')
const trafficSource = url.searchParams.get('traffic_source')

if (email) {
  localStorage.setItem('userEmail', email)
}
if (fbc) {
  localStorage.setItem('_fbc', fbc)
}

if (trafficSource) {
  localStorage.setItem('trafficSource', trafficSource)
}

const secondPixel = window.localStorage.getItem('pixelVersion')

if (window.location.pathname.startsWith('/intro1') || secondPixel === 'v2') {
  ReactPixel.init(secondPixelId)
  window.localStorage.setItem('pixelVersion', 'v2')
} else {
  ReactPixel.init(mainPixelId)
  window.localStorage.setItem('pixelVersion', 'v1')
}

amplitude.init('c8c7c772e17144633dd177d621ed0f0c', { defaultTracking: true })
const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 })
amplitude.add(sessionReplayTracking)

async function storeCountryDetails() {
  const existing = localStorage.getItem('countryCode')
  if (existing) return
  try {
    const countryData = await getCountryCode()
    const country = countryData?.country || 'US'
    const eu = isEU(country)

    if (eu) {
      localStorage.setItem('region', 'EU')
    } else if (country === 'US') {
      localStorage.setItem('region', 'US')
    } else {
      localStorage.setItem('region', country)
    }

    localStorage.setItem('countryCode', country)
    localStorage.setItem('postalCode', countryData?.postal || '90071')
    localStorage.setItem('ip', countryData?.ip || '')
  } catch (e) {
    localStorage.setItem('countryCode', 'US')
    localStorage.setItem('postalCode', '90071')
  }
}

const Redirect = () => {
  window.location.href = 'https://help.clean-master.app'
  return null
}

async function bootstrap() {
  await storeCountryDetails()
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

  root.render(
    <Router>
      <Suspense>
        <Layout>
          <Routes>
            <Route path="/" Component={() => <Redirect />} />
            <Route path="/intro" Component={(props) => <Intro {...props} />} />
            <Route path="/intro1" Component={(props) => <Intro {...props} />} />
            <Route path="/scan-results" Component={(props) => <ScanResults {...props} />} />
            <Route path="/email" Component={(props) => <EmailForm {...props} />} />
            <Route
              path="/quiz/:step"
              Component={(props) => <QuizStep url="/quiz/" quiz={Quiz} {...props} />}
            />
            <Route
              path="/quiz/alternative/:step"
              Component={(props) => (
                <QuizStep
                  version="alternative"
                  url="/quiz/alternative/"
                  quiz={AlternativeQuiz}
                  {...props}
                />
              )}
            />
            <Route
              path="/paywall"
              Component={(props) => (
                <Paywall
                  paywallFile={
                    process.env.REACT_APP_MODE !== 'dev' ? 'paywall-prod.json' : 'paywall-dev.json'
                  }
                  {...props}
                />
              )}
            />

            <Route
              path="/upsell"
              Component={(props) => (
                <Upsell
                  upsellFile={
                    process.env.REACT_APP_MODE !== 'dev' ? 'upsell-prod.json' : 'upsell-dev.json'
                  }
                  {...props}
                />
              )}
            />

            <Route path="/terms-of-use" Component={(props) => <TermsOfUse {...props} />} />
            <Route path="/us/terms-of-use" Component={(props) => <TermsOfUseUS {...props} />} />
            <Route path="/eu/terms-of-use" Component={(props) => <TermsOfUseEU {...props} />} />
            <Route path="/privacy-policy" Component={(props) => <PrivacyPolicy {...props} />} />
            <Route
              path="/us/privacy-policy"
              Component={(props) => <PrivacyPolicyUS {...props} />}
            />
            <Route
              path="/eu/privacy-policy"
              Component={(props) => <PrivacyPolicyEU {...props} />}
            />
            <Route path="/terms-of-use-app" Component={(props) => <TermsOfUseApp {...props} />} />
            <Route path="/subscription-terms-app" Component={(props) => <TermsApp {...props} />} />

            <Route path="/terms-of-use-quiz" Component={(props) => <TermsOfUseQuiz {...props} />} />
            <Route
              path="/privacy-policy-quiz"
              Component={(props) => <PrivacyPolicyQuiz {...props} />}
            />

            <Route
              path="/privacy-policy-app"
              Component={(props) => <PrivacyPolicyApp {...props} />}
            />
            <Route path="/fail-payment" Component={(props) => <PaymentResult {...props} />} />
            <Route path="/thank-you" Component={(props) => <ThankYou {...props} />} />
            <Route
              path="/test-payment-form"
              Component={(props) => (
                <TestPaymentFormPage
                  paywallFile={
                    process.env.REACT_APP_MODE !== 'dev' ? 'paywall-prod.json' : 'paywall-dev.json'
                  }
                  {...props}
                />
              )}
            />

            {/* NEW ROUTING */}
            <Route path="/new/intro" Component={(props) => <Intro version="brand" {...props} />} />
            <Route
              path="/new"
              Component={(props) => <Intro version="brand" isWhitePage={true} {...props} />}
            />
            <Route path="/new/intro1" Component={(props) => <Intro version="brand" {...props} />} />
            <Route
              path="/new/scan-results"
              Component={(props) => <ScanResults version="brand" {...props} />}
            />
            <Route
              path="/new/email"
              Component={(props) => <EmailForm version="brand" {...props} />}
            />
            <Route
              path="/new/paywall"
              Component={(props) => (
                <Paywall
                  version="brand"
                  paywallFile={
                    process.env.REACT_APP_MODE !== 'dev' ? 'paywall-prod.json' : 'paywall-dev.json'
                  }
                  {...props}
                />
              )}
            />
            <Route
              path="/new/paywall-one-plan"
              Component={(props) => (
                <PaywallOnePlan
                  version="brand"
                  paywallFile={
                    process.env.REACT_APP_MODE !== 'dev' ? 'paywall-prod.json' : 'paywall-dev.json'
                  }
                  {...props}
                />
              )}
            />
            <Route
              path="/new/thank-you"
              Component={(props) => <ThankYou version="brand" {...props} />}
            />
          </Routes>
        </Layout>
      </Suspense>
    </Router>,
  )
}

bootstrap()
